class SubscriptionComponent extends HTMLElement {
  constructor() {
    super();

    this.sellingPlanInput = null;

    this.oneTimeOption = null;
    this.subscriptionOption = null;

    this.oneTimeCard = null;
    this.subscriptionCard = null;

    this.frequencySelect = null;

    this.oneTimePriceContainer = null;
    this.subscriptionPriceContainer = null;

    this.savingBadge = null;
    this.deliveryText = null;

    this.productForm = null;
    this.quantityBreaks = null;

    this.handleOneTimeChange = () => {
      this.onOptionChange('one_time');
    };

    this.handleSubscriptionChange = () => {
      this.onOptionChange('subscription');
    };

    this.handleQuantityBreakUpdate = (event) => {
      this.onQuantityBreakUpdate(event);
    };
  }


  /* =========================================
     INITIALISATION
  ========================================= */

  connectedCallback() {
    this.initDomReferences();

    if (!this.frequencySelect) {
      console.warn(
        '[Subscription] Aucun selling plan disponible.'
      );

      return;
    }

    this.attachListeners();

    /*
     * D'abord :
     * abonnement / achat unique.
     */
    this.initDefaultSelection();

    /*
     * Ensuite :
     * synchronisation avec le pack.
     */
    this.syncPlanWithQuantity();

    this.updateDeliveryText();

    this.updateDisplayedPrices();

    this.syncStickySellingPlan();
  }


  disconnectedCallback() {
    this.detachListeners();
  }


  /* =========================================
     RÉFÉRENCES DOM
  ========================================= */

  initDomReferences() {
    this.sellingPlanInput =
      this.querySelector(
        '[data-ref="selling-plan-input"]'
      );

    this.oneTimeOption =
      this.querySelector(
        '[data-ref="one-time-option"]'
      );

    this.subscriptionOption =
      this.querySelector(
        '[data-ref="subscription-option"]'
      );

    this.oneTimeCard =
      this.querySelector(
        '[data-ref="one-time-card"]'
      );

    this.subscriptionCard =
      this.querySelector(
        '[data-ref="subscription-card"]'
      );

    this.frequencySelect =
      this.querySelector(
        '[data-ref="frequency-select"]'
      );

    this.oneTimePriceContainer =
      this.querySelector(
        '[data-ref="one-time-price"]'
      );

    this.subscriptionPriceContainer =
      this.querySelector(
        '[data-ref="subscription-price"]'
      );

    this.savingBadge =
      this.querySelector(
        '[data-ref="subscription-saving-badge"]'
      );

    this.deliveryText =
      this.querySelector(
        '[data-ref="subscription-delivery-text"]'
      );


    /*
     * PRODUCT FORM PRINCIPAL
     *
     * On cherche d'abord le formulaire produit
     * qui contient réellement l'input variant.
     */
    this.productForm =
      this.closest('.product-single')
        ?.querySelector(
          'form[action*="/cart/add"]'
        ) ||
      document.querySelector(
        'form[action*="/cart/add"]:not(#AddToCartForm--atc-sticky)'
      );


    /*
     * QUANTITY BREAKS
     */
    this.quantityBreaks =
      document.querySelector(
        'quantity-breaks'
      );
  }


  /* =========================================
     LISTENERS
  ========================================= */

  attachListeners() {
    this.oneTimeOption?.addEventListener(
      'change',
      this.handleOneTimeChange
    );

    this.subscriptionOption?.addEventListener(
      'change',
      this.handleSubscriptionChange
    );

    document.addEventListener(
      'quantity-breaks:updated',
      this.handleQuantityBreakUpdate
    );
  }


  detachListeners() {
    this.oneTimeOption?.removeEventListener(
      'change',
      this.handleOneTimeChange
    );

    this.subscriptionOption?.removeEventListener(
      'change',
      this.handleSubscriptionChange
    );

    document.removeEventListener(
      'quantity-breaks:updated',
      this.handleQuantityBreakUpdate
    );
  }


  /* =========================================
     CHANGEMENT DE PACK
  ========================================= */

  onQuantityBreakUpdate() {
    this.syncPlanWithQuantity();

    this.updateDeliveryText();

    this.updateDisplayedPrices();

    this.syncStickySellingPlan();

    this.dispatchSubscriptionUpdated();
  }


  /* =========================================
     QUANTITÉ SÉLECTIONNÉE
  ========================================= */

  getSelectedQuantity() {
    /*
     * 1. Méthode publique du custom element
     */
    if (
      this.quantityBreaks &&
      typeof this.quantityBreaks
        .getSelectedQuantity === 'function'
    ) {
      const selected =
        this.quantityBreaks
          .getSelectedQuantity();

      const quantity =
        parseInt(
          selected?.dataset?.quantity,
          10
        );

      if (quantity > 0) {
        return quantity;
      }
    }


    /*
     * 2. Fallback :
     * chercher directement la carte sélectionnée.
     */
    const selectedItem =
      document.querySelector(
        'quantity-break-item.quantity-break--selected'
      );

    const selectedQuantity =
      parseInt(
        selectedItem?.dataset?.quantity,
        10
      );

    if (selectedQuantity > 0) {
      return selectedQuantity;
    }


    /*
     * 3. Fallback formulaire.
     */
    const quantityInput =
      this.productForm?.querySelector(
        '[name="quantity"]'
      );

    const formQuantity =
      parseInt(
        quantityInput?.value,
        10
      );

    if (formQuantity > 0) {
      return formQuantity;
    }

    return 1;
  }


  /* =========================================
     PACK -> SELLING PLAN
  ========================================= */

  syncPlanWithQuantity() {
    if (!this.frequencySelect) {
      return;
    }

    const quantity =
      this.getSelectedQuantity();

    const options =
      Array.from(
        this.frequencySelect.options
      );


    /*
     * Cherche le chiffre correspondant
     * dans le nom Kaching.
     *
     * Exemple :
     *
     * "Livraison tous les 1 mois"
     * "Livraison tous les 3 mois"
     * "Livraison tous les 5 mois"
     *
     * => 1 / 3 / 5
     */
    let matchingOption =
      options.find((option) => {

        /*
         * Si data-plan-quantity existe,
         * priorité à lui.
         */
        const explicitQuantity =
          parseInt(
            option.dataset.planQuantity,
            10
          );

        if (
          explicitQuantity > 0 &&
          explicitQuantity === quantity
        ) {
          return true;
        }

        const name =
          (
            option.dataset.planName ||
            option.textContent ||
            ''
          )
            .trim()
            .toLowerCase();

        const match =
          name.match(/\d+/);

        if (!match) {
          return false;
        }

        return (
          parseInt(
            match[0],
            10
          ) === quantity
        );
      });


    /*
     * Si aucun plan exact :
     * on ne force PAS le premier plan.
     *
     * C'est plus sûr ici :
     * un pack 5 ne doit pas envoyer
     * accidentellement un plan 1 mois.
     */
    if (!matchingOption) {
      console.warn(
        `[Subscription] Aucun selling plan correspondant au pack ${quantity}.`
      );

      this.updateSellingPlanInput('');

      this.syncStickySellingPlan();

      return;
    }


    this.frequencySelect.value =
      matchingOption.value;


    /*
     * Le vrai input Shopify ne reçoit
     * le selling_plan QUE si abonnement actif.
     */
    if (this.isSubscriptionSelected()) {
      this.updateSellingPlanInput(
        matchingOption.value
      );
    } else {
      this.updateSellingPlanInput('');
    }

    this.updateSavingBadge();
  }


  /* =========================================
     SÉLECTION PAR DÉFAUT
  ========================================= */

  initDefaultSelection() {
    const requiresSellingPlan =
      this.dataset.requiresSellingPlan ===
      'true';


    /*
     * Produit abonnement obligatoire.
     */
    if (
      requiresSellingPlan &&
      this.subscriptionOption
    ) {
      this.subscriptionOption.checked =
        true;

      this.onOptionChange(
        'subscription',
        false
      );

      return;
    }


    const defaultSelected =
      this.dataset.defaultSelected ||
      'subscription';


    if (
      defaultSelected ===
        'subscription' &&
      this.subscriptionOption
    ) {
      this.subscriptionOption.checked =
        true;

      this.onOptionChange(
        'subscription',
        false
      );

      return;
    }


    if (this.oneTimeOption) {
      this.oneTimeOption.checked =
        true;

      this.onOptionChange(
        'one_time',
        false
      );
    }
  }


  /* =========================================
     ABONNEMENT / ACHAT UNIQUE
  ========================================= */

  onOptionChange(
    optionType,
    notify = true
  ) {
    this.updateSelectedCard(
      optionType
    );


    if (
      optionType ===
      'subscription'
    ) {
      /*
       * On synchronise d'abord le plan
       * avec le pack actif.
       */
      this.syncPlanWithQuantity();

      this.updateSellingPlanInput(
        this.getSelectedFrequencyValue()
      );
    } else {
      /*
       * Achat unique :
       * aucun selling_plan.
       */
      this.updateSellingPlanInput('');
    }


    this.updateDisplayedPrices();

    this.syncStickySellingPlan();


    if (notify) {
      this.dispatchSubscriptionUpdated();
    }
  }


  updateSelectedCard(optionType) {
    this.oneTimeCard?.classList.remove(
      'subscription__option--selected'
    );

    this.subscriptionCard?.classList.remove(
      'subscription__option--selected'
    );


    if (
      optionType ===
      'subscription'
    ) {
      this.subscriptionCard?.classList.add(
        'subscription__option--selected'
      );
    } else {
      this.oneTimeCard?.classList.add(
        'subscription__option--selected'
      );
    }
  }


  /* =========================================
     SELLING PLAN
  ========================================= */

  getSelectedFrequencyValue() {
    return (
      this.frequencySelect?.value ||
      ''
    );
  }


  getSelectedFrequencyElement() {
    if (!this.frequencySelect) {
      return null;
    }

    return (
      this.frequencySelect.options[
        this.frequencySelect.selectedIndex
      ] ||
      null
    );
  }


  updateSellingPlanInput(value) {
    if (!this.sellingPlanInput) {
      return;
    }

    this.sellingPlanInput.value =
      value || '';
  }


  /* =========================================
     SYNCHRONISATION STICKY ATC
  ========================================= */

  syncStickySellingPlan() {
    const stickyForm =
      document.querySelector(
        '#AddToCartForm--atc-sticky'
      );

    if (!stickyForm) {
      return;
    }


    let stickySellingPlan =
      stickyForm.querySelector(
        '[name="selling_plan"]'
      );


    if (!stickySellingPlan) {
      stickySellingPlan =
        document.createElement(
          'input'
        );

      stickySellingPlan.type =
        'hidden';

      stickySellingPlan.name =
        'selling_plan';

      stickyForm.appendChild(
        stickySellingPlan
      );
    }


    stickySellingPlan.value =
      this.isSubscriptionSelected()
        ? this.getSelectedSellingPlan()
        : '';
  }


  /* =========================================
     AJUSTEMENT SELLING PLAN
  ========================================= */

  getSelectedPlanAdjustment() {
    const selectedElement =
      this.getSelectedFrequencyElement();

    if (!selectedElement) {
      return null;
    }

    return {
      type:
        selectedElement.dataset
          .adjustmentType ||
        'percentage',

      value:
        parseFloat(
          selectedElement.dataset
            .adjustmentValue
        ) || 0
    };
  }


  /* =========================================
     DONNÉES DU PACK
  ========================================= */

  getSelectedPack() {
    /*
     * Méthode principale.
     */
    if (
      this.quantityBreaks &&
      typeof this.quantityBreaks
        .getSelectedQuantity ===
        'function'
    ) {
      const item =
        this.quantityBreaks
          .getSelectedQuantity();

      if (item) {
        return item;
      }
    }


    /*
     * Fallback DOM.
     */
    return document.querySelector(
      'quantity-break-item.quantity-break--selected'
    );
  }


  getBasePrices() {
    const selectedPack =
      this.getSelectedPack();


    /*
     * Avec quantity-break sélectionné.
     */
    if (selectedPack) {
      const quantity =
        parseInt(
          selectedPack.dataset.quantity,
          10
        ) || 1;


      const priceAfterQB =
        parseInt(
          selectedPack.dataset.totalPrice,
          10
        ) || 0;


      const compareAtPrice =
        parseInt(
          selectedPack.dataset
            .totalCompareAtPrice,
          10
        ) ||
        priceAfterQB;


      /*
       * Dans notre nouveau système,
       * data-total-price représente déjà
       * le prix achat unique final du pack.
       *
       * Il nous faut également retrouver
       * le prix théorique AVANT remise QB
       * pour appliquer Kaching dans le bon ordre.
       */
      let unitPrice = 0;


      /*
       * Pack 1 :
       * son total = prix unitaire.
       */
      const packOne =
        this.quantityBreaks
          ?.querySelector(
            'quantity-break-item[data-quantity="1"]'
          ) ||
        document.querySelector(
          'quantity-break-item[data-quantity="1"]'
        );


      if (packOne) {
        unitPrice =
          parseInt(
            packOne.dataset.totalPrice,
            10
          ) || 0;
      }


      /*
       * Fallback :
       * prix principal actuel / quantité.
       */
      if (!unitPrice) {
        const productPrice =
          document.querySelector(
            '#ProductPrice'
          );

        const contentValue =
          parseFloat(
            productPrice?.getAttribute(
              'content'
            )
          );

        if (
          !Number.isNaN(
            contentValue
          )
        ) {
          unitPrice =
            Math.round(
              contentValue * 100
            );
        }
      }


      const priceBeforeQB =
        unitPrice > 0
          ? unitPrice * quantity
          : compareAtPrice;


      return {
        priceAfterQB,
        priceBeforeQB,
        compareAtPrice,
        quantity
      };
    }


    /*
     * Sans quantity break.
     */
    const productPrice =
      document.querySelector(
        '#ProductPrice'
      );

    const comparePrice =
      document.querySelector(
        '#ComparePrice'
      );


    const price =
      this.parseMoneyElement(
        productPrice
      );


    const compareAt =
      this.parseMoneyElement(
        comparePrice
      ) || price;


    return {
      priceAfterQB:
        price,

      priceBeforeQB:
        price,

      compareAtPrice:
        compareAt,

      quantity:
        1
    };
  }


  /* =========================================
     PRIX DES DEUX CARTES
  ========================================= */

  updateDisplayedPrices() {
    const {
      priceAfterQB,
      priceBeforeQB,
      compareAtPrice,
      quantity
    } = this.getBasePrices();


    if (!priceAfterQB) {
      return;
    }


    /* -----------------------------------------
       ACHAT UNIQUE
    ----------------------------------------- */

    const oneTimeTotal =
      priceAfterQB;


    const oneTimeCompareAt =
      compareAtPrice;


    this.updatePriceElement(
      this.oneTimePriceContainer,
      oneTimeTotal,
      oneTimeCompareAt
    );


    /* -----------------------------------------
       ABONNEMENT
    ----------------------------------------- */

    const adjustment =
      this.getSelectedPlanAdjustment();


    let subscriptionPrice =
      this.applyPriceAdjustment(
        priceBeforeQB,
        adjustment,
        quantity
      );


    /*
     * IMPORTANT :
     *
     * Dans notre quantité break actuel,
     * les packs 3 et 5 contiennent déjà
     * une remise pack :
     *
     * 3 => 2 payés
     * 5 => 3 payés
     *
     * On doit donc conserver le même ratio
     * après application de Kaching.
     */


    /*
     * Pour les adjustments Kaching
     * percentage / fixed_amount :
     *
     * on applique ensuite la réduction
     * du quantity break.
     *
     * Pour "price", Kaching donne déjà
     * un prix fixe PAR ARTICLE.
     */
    if (
      adjustment?.type !== 'price'
    ) {
      const packRatio =
        priceBeforeQB > 0
          ? priceAfterQB /
            priceBeforeQB
          : 1;


      subscriptionPrice =
        Math.round(
          subscriptionPrice *
          packRatio
        );
    }


    /*
     * Pour adjustment "price":
     * value = prix fixe PAR ARTICLE.
     *
     * Pour conserver également le pack
     * 2+1 / 3+2, on applique ici aussi
     * le ratio du pack.
     *
     * Si Kaching est configuré avec un
     * PRIX FINAL déjà prévu pour le pack,
     * il faudra simplement retirer ce bloc.
     */
    


    subscriptionPrice =
      Math.max(
        0,
        subscriptionPrice
      );


    const subscriptionCompareAt =
      compareAtPrice;


    this.updatePriceElement(
      this.subscriptionPriceContainer,
      subscriptionPrice,
      subscriptionCompareAt
    );


    this.updateSavingBadge();


    /*
     * Maintenant on met aussi le prix actif
     * partout dans le thème.
     */
    if (
      this.isSubscriptionSelected()
    ) {
      this.updateThemePrices(
        subscriptionPrice,
        subscriptionCompareAt
      );
    } else {
      this.updateThemePrices(
        oneTimeTotal,
        oneTimeCompareAt
      );
    }
  }


  /* =========================================
     BADGE ÉCONOMIE
  ========================================= */

  updateSavingBadge() {
    if (!this.savingBadge) {
      return;
    }


    const {
      priceAfterQB,
      priceBeforeQB,
      quantity
    } = this.getBasePrices();


    const adjustment =
      this.getSelectedPlanAdjustment();


    if (!adjustment) {
      this.savingBadge.textContent =
        '';

      return;
    }


    let subscriptionPrice =
      this.applyPriceAdjustment(
        priceBeforeQB,
        adjustment,
        quantity
      );


    if (
      adjustment?.type !== 'price'
    ) {
      const packRatio =
        priceBeforeQB > 0
          ? priceAfterQB /
            priceBeforeQB
          : 1;

      subscriptionPrice =
        Math.round(
          subscriptionPrice *
          packRatio
        );
    }

    const saving =
      Math.max(
        0,
        priceAfterQB -
          subscriptionPrice
      );


    this.savingBadge.textContent =
      saving > 0
        ? `Économisez ${this.formatMoney(
            saving
          )}`
        : '';
  }


  /* =========================================
     TEXTE LIVRAISON
  ========================================= */

  updateDeliveryText() {
    if (!this.deliveryText) {
      return;
    }

    const quantity =
      this.getSelectedQuantity();


    if (quantity <= 1) {
      this.deliveryText.textContent =
        'Recevez votre cure tous les mois';

      return;
    }


    this.deliveryText.textContent =
      `Recevez votre cure tous les ${quantity} mois`;
  }


  /* =========================================
     APPLICATION REMISE KACHING
  ========================================= */

  applyPriceAdjustment(
    basePrice,
    adjustment,
    quantity = 1
  ) {
    if (!adjustment) {
      return basePrice;
    }


    switch (
      adjustment.type
    ) {
      case 'percentage':

        return Math.round(
          basePrice *
            (
              100 -
              adjustment.value
            ) /
            100
        );


      case 'fixed_amount':

        /*
         * Remise fixe PAR ARTICLE.
         */
        return Math.max(
          0,
          basePrice -
            (
              adjustment.value *
              quantity
            )
        );


      case 'price':

  /*
 * Prix fixe PAR ARTICLE.
 */
return (
  adjustment.value *
  quantity
);


      default:
        return basePrice;
    }
  }


  /* =========================================
     UPDATE PRICE ELEMENT
  ========================================= */

  updatePriceElement(
    container,
    price,
    compareAtPrice
  ) {
    if (!container) {
      return;
    }


    const priceEl =
      container.querySelector(
        '[data-ref="price"]'
      );


    const compareAtPriceEl =
      container.querySelector(
        '[data-ref="compare-at-price"]'
      );


    if (priceEl) {
      priceEl.textContent =
        this.formatMoney(
          price
        );

      priceEl.dataset.value =
        price;
    }


    if (compareAtPriceEl) {

      if (
        compareAtPrice > price
      ) {
        compareAtPriceEl.textContent =
          this.formatMoney(
            compareAtPrice
          );

        compareAtPriceEl.dataset.value =
          compareAtPrice;
      } else {
        compareAtPriceEl.textContent =
          '';

        compareAtPriceEl.dataset.value =
          '';
      }
    }
  }


  /* =========================================
     MISE À JOUR DU THÈME
  ========================================= */

  updateThemePrices(
    price,
    compareAtPrice
  ) {
    const formattedPrice =
      this.formatMoney(
        price
      );


    const formattedCompareAtPrice =
      this.formatMoney(
        compareAtPrice
      );


    /* -----------------------------------------
       PRIX PRINCIPAL
    ----------------------------------------- */

    const productPrice =
      document.querySelector(
        '#ProductPrice'
      );


    const comparePrice =
      document.querySelector(
        '#ComparePrice'
      );


    const compareWrapper =
      comparePrice?.closest(
        '.product-single__price--wrapper'
      );


    if (productPrice) {
      productPrice.textContent =
        formattedPrice;

      productPrice.setAttribute(
        'content',
        (
          price /
          100
        ).toFixed(2)
      );


      if (
        compareAtPrice > price
      ) {
        productPrice.classList.add(
          'price_on_sale'
        );
      } else {
        productPrice.classList.remove(
          'price_on_sale'
        );
      }
    }


    if (comparePrice) {

      if (
        compareAtPrice > price
      ) {
        comparePrice.textContent =
          formattedCompareAtPrice;

        comparePrice.style.display =
          '';

        compareWrapper?.setAttribute(
          'aria-hidden',
          'false'
        );
      } else {
        comparePrice.textContent =
          '';

        comparePrice.style.display =
          'none';

        compareWrapper?.setAttribute(
          'aria-hidden',
          'true'
        );
      }
    }


    /* -----------------------------------------
       BOUTON ATC PRINCIPAL
    ----------------------------------------- */

    const mainAtcButton =
      document.querySelector(
        '.product-single__add-to-cart button[name="add"]'
      );


    if (mainAtcButton) {

      if (
        !mainAtcButton.dataset
          .subscriptionBaseText
      ) {
        const currentText =
          mainAtcButton
            .textContent
            .trim();


        mainAtcButton.dataset
          .subscriptionBaseText =
          currentText
            .replace(
              /\s*-\s*[\d\s,.]+(?:€|EUR).*$/i,
              ''
            )
            .trim() ||
          'Ajouter au panier';
      }


      mainAtcButton.textContent =
        `${mainAtcButton.dataset.subscriptionBaseText} - ${formattedPrice}`;
    }


    /* -----------------------------------------
       BOUTON STICKY
    ----------------------------------------- */

    const stickyAtcButton =
      document.querySelector(
        '#AddToCart--atc-sticky'
      );


    if (stickyAtcButton) {
      const stickyText =
        stickyAtcButton.querySelector(
          '.btn__text'
        );


      if (stickyText) {
        stickyText.textContent =
          `Ajouter - ${formattedPrice}`;
      }
    }
  }


  /* =========================================
     HELPERS
  ========================================= */

  formatMoney(cents) {
    return new Intl.NumberFormat(
      'fr-FR',
      {
        style:
          'currency',

        currency:
          'EUR'
      }
    ).format(
      cents /
      100
    );
  }


  parseMoneyElement(element) {
    if (!element) {
      return 0;
    }


    const content =
      parseFloat(
        element.getAttribute(
          'content'
        )
      );


    if (
      !Number.isNaN(
        content
      )
    ) {
      return Math.round(
        content *
        100
      );
    }


    const text =
      element.textContent
        .replace(
          /\s/g,
          ''
        )
        .replace(
          ',',
          '.'
        )
        .replace(
          /[^\d.-]/g,
          ''
        );


    const parsed =
      parseFloat(
        text
      );


    return Number.isNaN(
      parsed
    )
      ? 0
      : Math.round(
          parsed *
          100
        );
  }


  /* =========================================
     EVENT
  ========================================= */

  dispatchSubscriptionUpdated() {
    document.dispatchEvent(
      new CustomEvent(
        'subscription:updated',
        {
          detail: {
            subscriptionSelected:
              this.isSubscriptionSelected(),

            sellingPlan:
              this.getSelectedSellingPlan(),

            quantity:
              this.getSelectedQuantity()
          }
        }
      )
    );
  }


  /* =========================================
     API PUBLIQUE
  ========================================= */

  getSelectedSellingPlan() {
    return (
      this.sellingPlanInput?.value ||
      ''
    );
  }


  isSubscriptionSelected() {
    return (
      this.subscriptionOption
        ?.checked ||
      false
    );
  }


  isSubscriptionActivated() {
    return (
      this.style.display !==
      'none'
    );
  }


  getPriceAdjustment() {
    if (
      !this.isSubscriptionSelected()
    ) {
      return null;
    }

    return (
      this.getSelectedPlanAdjustment()
    );
  }


  getPlanAdjustmentForQuantity(
    quantity
  ) {
    if (!this.frequencySelect) {
      return null;
    }


    const options =
      Array.from(
        this.frequencySelect.options
      );


    let matchingOption =
      options.find((option) => {

        const explicitQuantity =
          parseInt(
            option.dataset
              .planQuantity,
            10
          );


        if (
          explicitQuantity ===
          parseInt(
            quantity,
            10
          )
        ) {
          return true;
        }


        const name =
          (
            option.dataset.planName ||
            option.textContent ||
            ''
          ).trim();


        const match =
          name.match(/\d+/);


        return (
          match &&
          parseInt(
            match[0],
            10
          ) ===
          parseInt(
            quantity,
            10
          )
        );
      });


    if (!matchingOption) {
      return null;
    }


    return {
      type:
        matchingOption.dataset
          .adjustmentType ||
        'percentage',

      value:
        parseFloat(
          matchingOption.dataset
            .adjustmentValue
        ) || 0
    };
  }


  calculateAdjustedPriceForQuantity(
    basePrice,
    quantity
  ) {
    const adjustment =
      this.getPlanAdjustmentForQuantity(
        quantity
      );


    return this.applyPriceAdjustment(
      basePrice,
      adjustment,
      quantity
    );
  }


  calculateAdjustedPrice(
    basePrice,
    quantity = 1
  ) {
    return this.applyPriceAdjustment(
      basePrice,
      this.getPriceAdjustment(),
      quantity
    );
  }
}


/* =========================================
   CUSTOM ELEMENT
========================================= */

if (
  !customElements.get(
    'subscription-component'
  )
) {
  customElements.define(
    'subscription-component',
    SubscriptionComponent
  );
}